import React from 'react'
import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import { P, Text } from '@mxenabled/mxui'

import { useGlobalCopyStore, useHoldingStore, useScreenSize } from '../../common'

import { AnalysisType } from '../../core/constants/Investments'

import AnalysisDetails from './AnalysisDetails'
import InfoTooltip from './InfoToolTip'

const Analysis: React.FC = () => {
  const {
    investments: { analysis: copy },
  } = useGlobalCopyStore()
  const { analysisChartData, analysisDetails, analysisType, setAnalysisType } = useHoldingStore()

  const isSmallMobileDevice = useMediaQuery('(min-width:320px) and (max-width:375px)')
  const { isMobile } = useScreenSize()

  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const { cellDimension, columns, rows, textVariant } = React.useMemo(() => {
    const isStocks = analysisType === AnalysisType.Stocks

    const columns = isStocks
      ? Object.values(copy.stock_column_labels)
      : Object.values(copy.bond_column_labels)
    const rows = isStocks
      ? Object.values(copy.stock_row_labels)
      : Object.values(copy.bond_row_labels)

    const textVariant: 'body1' | 'caption' = isMobile ? 'caption' : 'body1'
    const cellDimension = isMobile ? 72 : 100

    return { cellDimension, columns, rows, textVariant }
  }, [copy, isMobile, analysisType])

  return (
    <Stack
      sx={{
        flexDirection: { md: 'row' },
        gap: isMobile ? 48 : 64,
        justifyContent: 'center',
        pt: 48,
        px: 16,
      }}
    >
      <Stack
        sx={{
          alignItems: 'center',
          flexDirection: 'row',
          justifyContent: 'center',
          pr: { lg: 72 },
        }}
      >
        <Stack
          sx={{
            alignItems: 'center',
            bottom: isMobile ? 10 : 24,
            gap: isMobile ? 0 : 24,
            position: 'relative',
            transform: 'rotate(-180deg)',
            writingMode: 'vertical-rl',
          }}
        >
          <P bold={true} color="secondary.main" variant={textVariant}>
            {analysisType === AnalysisType.Stocks
              ? copy.market_capitalization
              : copy.credit_quality}
          </P>
          <Stack
            sx={{
              flexDirection: 'row',
              height: isMobile ? 224 : 308,
              justifyContent: 'space-around',
            }}
          >
            {columns.map((label) => (
              <Text
                key={label}
                sx={{
                  color: 'secondary.main',
                  pl: 12,
                  pr: isMobile ? 4 : 0,
                }}
                variant={textVariant}
              >
                {label}
              </Text>
            ))}
          </Stack>
        </Stack>
        <Stack sx={{ gap: 4 }}>
          <Stack
            sx={{
              alignSelf: isSmallMobileDevice ? 'start' : 'center',
              flexDirection: 'row',
              gap: 8,
              pb: 12,
            }}
          >
            <Text bold={true}>{copy.invest_analysis}</Text>
            <InfoTooltip
              tooltip_info_body={copy.tooltip_info_body}
              tooltip_info_title={copy.tooltip_info_title}
            />
          </Stack>
          {analysisChartData.map((row, rowIndex, data) => (
            <Stack key={rowIndex} sx={{ flexDirection: 'row', gap: 4 }}>
              {row.map((value, colIndex) => {
                const isFirstRow = rowIndex === 0
                const isLastRow = rowIndex === data.length - 1
                const isFirstCol = colIndex === 0
                const isLastCol = colIndex === row.length - 1

                const isHigh = Number(value.replace('%', '')) >= 25

                let bgcolor = 'grey.200'
                let color = 'grey.900'

                if (isDark) {
                  bgcolor = isHigh ? 'grey.200' : 'grey.700'
                  color = isHigh ? 'grey.900' : 'common.white'
                } else {
                  bgcolor = isHigh ? 'grey.700' : 'grey.200'
                  color = isHigh ? 'common.white' : 'grey.900'
                }

                const borderRadius = {
                  borderBottomLeftRadius: isLastRow && isFirstCol ? 4 : 0,
                  borderBottomRightRadius: isLastRow && isLastCol ? 4 : 0,
                  borderTopLeftRadius: isFirstRow && isFirstCol ? 4 : 0,
                  borderTopRightRadius: isFirstRow && isLastCol ? 4 : 0,
                }
                return (
                  <Stack
                    key={colIndex}
                    sx={{
                      ...borderRadius,
                      alignItems: 'center',
                      bgcolor,
                      height: cellDimension,
                      justifyContent: 'center',
                      width: cellDimension,
                    }}
                  >
                    <Text
                      sx={{
                        color,
                        fontWeight: isMobile ? 600 : 700,
                      }}
                      variant={isMobile ? 'body1' : 'h3'}
                    >
                      {value}
                    </Text>
                  </Stack>
                )
              })}
            </Stack>
          ))}
          <Stack sx={{ alignSelf: 'center', flexDirection: 'row', pt: 12 }}>
            {rows.map((label) => (
              <Stack key={label} sx={{ alignItems: 'center', width: cellDimension }}>
                <Text color="secondary.main" variant={textVariant}>
                  {label}
                </Text>
              </Stack>
            ))}
          </Stack>
          <Text
            bold={true}
            sx={{ color: 'secondary.main', pt: isMobile ? 4 : 24, textAlign: 'center' }}
            variant={textVariant}
          >
            {analysisType === AnalysisType.Stocks ? copy.valuation : copy.duration}
          </Text>
        </Stack>
      </Stack>
      <AnalysisDetails
        analysisDetails={analysisDetails}
        analysisType={analysisType}
        copy={{
          empty_data: copy.empty_data,
          equities: copy.equities,
          bonds: copy.bonds,
        }}
        setAnalysisType={setAnalysisType}
      />
    </Stack>
  )
}

export default observer(Analysis)
